import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import LocationConsentModal from './common/LocationConsentModal';
import PolicyNoticeBanner from './common/PolicyNoticeBanner';
import { POLICY_NOTICES, LBS_TERMS_V11_KEY } from '../config/policyNotices';
import {
    LOCATION_SERVICES, readLocationConsent, writeLocationConsent, clearLocationConsent, requestBrowserLocation,
    LOCATION_REVOKE_OK, LOCATION_REVOKE_FAIL, LOCATION_REGION_HINT,
} from '../utils/locationConsent';
import {
    Building2, MapPin, CheckCircle, Search, ClipboardList,
    Info, Send, ArrowLeft, Clock, Handshake
} from 'lucide-react';

const LOC_SERVICE = LOCATION_SERVICES.asRemake.key;

/**
 * 치과 로그인 — A/S 재제작 상담·연결 신청 (/as-remake)
 * - 회원 기공소 명단(연락처 비노출)에서 여러 곳 선택 → 재제작 내용 입력 → 신청
 * - 신청 후 덴탈클러스터(관리자)가 확인해 1곳을 배정·연결 (직접 연결 아님, 중개)
 * - "재제작 상담·연결" 표기 (무료/보장/검증/추천 표현 금지 — 의료법/법적 검토 대기)
 * - 환자 실명 미수집: 케이스번호·치식 위주(환자정보 최소화)
 * - 기존 /test-cases 공개 게시판을 대체하는 치과 진입점 (기존 라우트·컴포넌트는 유지)
 */

const REGIONS = ['전체', '서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];

// 주문 의뢰서(CreateOrder)와 동일한 기본 보철 종류
const PROSTHESIS_TYPES = ['크라운', '브릿지', '인레이/온레이', '임플란트 보철', '틀니/의치', '교정장치', '라미네이트', '기타'];
const REMAKE_REASONS = ['파손', '색상 불일치', '교합 문제', '적합도(마진) 문제', '형태 수정', '환자 요청', '기타'];

function AsRemakeRequest({ user }) {
    const navigate = useNavigate();

    const [labs, setLabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [regionFilter, setRegionFilter] = useState('전체');
    const [userLocation, setUserLocation] = useState(null);

    // 선택된 기공소 id 집합
    const [selectedIds, setSelectedIds] = useState([]);

    // 재제작 내용
    const [prosthesisType, setProsthesisType] = useState('');
    const [remakeReason, setRemakeReason] = useState('');
    const [teeth, setTeeth] = useState('');       // 예: #16, #26
    const [shade, setShade] = useState('');        // 예: A2
    const [notes, setNotes] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // 내 신청 목록
    const [myRequests, setMyRequests] = useState([]);

    const clinicId = user?.companyId || user?.uid;

    // 위치 이용(거리순 정렬) — 화면 진입 시 자동 요청 없음. [현재 위치로 찾기] → 안내창 동의 후에만 요청(약관 v1.1 제3조 ③)
    const [showLocConsent, setShowLocConsent] = useState(false);
    const [locating, setLocating] = useState(false);
    const [locNotice, setLocNotice] = useState('');

    const askLocation = () => {
        setLocating(true);
        requestBrowserLocation(
            (loc) => { setUserLocation(loc); setLocating(false); setLocNotice(''); },
            (msg) => { setLocating(false); setLocNotice(msg); },
        );
    };
    const onLocationButton = () => {
        setLocNotice('');
        if (readLocationConsent(LOC_SERVICE)) { askLocation(); return; }
        setShowLocConsent(true);
    };
    const agreeLocConsent = () => { writeLocationConsent(LOC_SERVICE); setShowLocConsent(false); askLocation(); };
    const declineLocConsent = () => { setShowLocConsent(false); setLocNotice(LOCATION_REGION_HINT); };
    const revokeLocConsent = () => {
        const ok = clearLocationConsent();
        setUserLocation(null);
        setLocNotice(ok ? LOCATION_REVOKE_OK : LOCATION_REVOKE_FAIL);
    };

    const getDistance = useCallback((lat1, lng1, lat2, lng2) => {
        if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }, []);

    const extractRegion = (address) => {
        if (!address) return '';
        const first = (address.trim().split(/\s+/)[0] || '').replace(/특별시|광역시|도$/g, '');
        return first;
    };

    // 회원 기공소 명단 로드 (PublicLabDirectory.loadLabs 로직 재사용 — 연락처 비노출)
    const loadLabs = useCallback(async () => {
        try {
            setLoading(true);
            const seen = new Set();
            const labsData = [];

            // 1) 홍보 등록 기공소 (labAdvertisements, isActive)
            try {
                const adsSnap = await getDocs(query(collection(db, 'labAdvertisements'), where('isActive', '==', true)));
                adsSnap.docs.forEach(d => {
                    if (seen.has(d.id)) return;
                    seen.add(d.id);
                    const a = d.data();
                    labsData.push({
                        id: d.id,
                        businessName: a.businessName || a.companyName || '기공소',
                        address: a.address || '',
                        slogan: a.slogan || '',
                        profileImageUrl: a.profileImageUrl || '',
                        subscriptionActive: a.subscriptionActive === true,
                    });
                });
            } catch (e) { console.warn('홍보 기공소 로드 실패:', e); }

            // 2) 홍보 미공개 회원 기공소 (users, businessType==lab)
            try {
                const labUsersSnap = await getDocs(query(collection(db, 'users'), where('businessType', '==', 'lab')));
                labUsersSnap.docs.forEach(d => {
                    const u = d.data();
                    if (seen.has(d.id)) return;
                    if (u.userType === 'staff') return;
                    if (u.hideFromDirectory === true) return;
                    if ((u.email || '').endsWith('@test.com')) return;
                    seen.add(d.id);
                    labsData.push({
                        id: d.id,
                        businessName: u.businessName || u.companyName || u.name || '기공소',
                        address: u.address || '',
                        slogan: '',
                        profileImageUrl: u.profileImageUrl || '',
                        _noAd: true,
                    });
                });
            } catch (e) { console.warn('회원 기공소 로드 실패:', e); }

            setLabs(labsData);
        } catch (e) {
            console.error('기공소 목록 로드 실패:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMyRequests = useCallback(async () => {
        if (!clinicId) return;
        try {
            const snap = await getDocs(query(collection(db, 'asRemakeRequests'), where('clinicId', '==', clinicId)));
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setMyRequests(rows);
        } catch (e) {
            console.warn('내 신청 목록 로드 실패:', e);
        }
    }, [clinicId]);

    useEffect(() => { loadLabs(); }, [loadLabs]);
    useEffect(() => { loadMyRequests(); }, [loadMyRequests]);

    // 필터 + 거리순(객관 기준) 정렬
    const visibleLabs = React.useMemo(() => {
        let list = labs;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(l =>
                (l.businessName || '').toLowerCase().includes(q) ||
                (l.address || '').toLowerCase().includes(q) ||
                (l.slogan || '').toLowerCase().includes(q)
            );
        }
        if (regionFilter !== '전체') {
            list = list.filter(l => extractRegion(l.address) === regionFilter);
        }
        if (userLocation) {
            list = [...list].sort((a, b) =>
                getDistance(userLocation.lat, userLocation.lng, a.lat, a.lng) -
                getDistance(userLocation.lat, userLocation.lng, b.lat, b.lng));
        }
        return list;
    }, [labs, searchQuery, regionFilter, userLocation, getDistance]);

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const canSubmit = selectedIds.length > 0 && prosthesisType && remakeReason && !submitting;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        if (!clinicId) { alert('로그인 정보를 확인할 수 없습니다.'); return; }
        setSubmitting(true);
        try {
            const selectedLabs = labs.filter(l => selectedIds.includes(l.id));
            const payload = {
                clinicId,
                clinicName: user?.businessName || user?.companyName || user?.name || '',
                clinicPhone: user?.phone || '',
                clinicEmail: user?.email || '',
                selectedLabIds: selectedLabs.map(l => l.id),
                selectedLabNames: selectedLabs.map(l => l.businessName || '기공소'),
                remakeInfo: {
                    prosthesisType,
                    remakeReason,
                    teeth: teeth.trim(),
                    shade: shade.trim(),
                    notes: notes.trim(),
                },
                status: 'pending',
                // assignedLabId·assignedLabName·adminNotes는 관리자/CF가 배정 시 세팅 — 생성 시 보내지 않음
                //   (rules가 생성 시 관리자 필드 주입을 차단하므로 여기서 null이라도 넣으면 신청이 거부됨. 2026-08-15 버그 수정)
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };
            await addDoc(collection(db, 'asRemakeRequests'), payload);
            setSubmitted(true);
            // 폼 초기화
            setSelectedIds([]);
            setProsthesisType(''); setRemakeReason(''); setTeeth(''); setShade(''); setNotes('');
            loadMyRequests();
        } catch (e) {
            console.error('신청 실패:', e);
            alert('신청에 실패했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setSubmitting(false);
        }
    };

    const statusLabel = { pending: '덴클 확인 대기', reviewing: '상담 중', assigned: '기공소 배정', connected: '연결됨', rejected: '반려', cancelled: '취소' };
    const statusColor = { pending: '#d97706', reviewing: '#2563eb', assigned: '#7c3aed', connected: '#059669', rejected: '#ef4444', cancelled: '#94a3b8' };
    const statusBg = { pending: '#fffbeb', reviewing: '#eff6ff', assigned: '#f5f3ff', connected: '#ecfdf5', rejected: '#fef2f2', cancelled: '#f8fafc' };

    const fmtDate = (ts) => {
        if (!ts) return '-';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    };

    // 신청 완료 안내
    if (submitted) {
        return (
            <div style={S.wrap}>
                <div style={S.doneCard}>
                    <div style={S.doneIcon}><CheckCircle size={40} color="#059669" /></div>
                    <h2 style={S.doneTitle}>신청이 접수되었습니다</h2>
                    <p style={S.doneDesc}>
                        {'덴탈클러스터가 확인 후 연결해 드립니다.'}<br />
                        {'선택하신 기공소와 협의해 한 곳을 연결해 드리며, 진행 상황은 아래 '}
                        <strong>{'내 신청 내역'}</strong>{'에서 확인하실 수 있습니다.'}
                    </p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20, flexWrap: 'wrap' }}>
                        <button style={S.primaryBtn} onClick={() => setSubmitted(false)}>새 재제작 상담 신청</button>
                        <button style={S.ghostBtn} onClick={() => navigate('/dashboard')}>대시보드로</button>
                    </div>
                    {myRequests.length > 0 && (
                        <div style={{ marginTop: 28, textAlign: 'left' }}>
                            <h3 style={S.sectionTitle}>내 신청 내역</h3>
                            {renderMyRequests()}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function renderMyRequests() {
        if (myRequests.length === 0) {
            return <div style={S.empty}>아직 신청 내역이 없습니다.</div>;
        }
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {myRequests.map(r => (
                    <div key={r.id} style={S.reqCard}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ ...S.statusBadge, color: statusColor[r.status] || '#64748b', background: statusBg[r.status] || '#f1f5f9' }}>
                                {statusLabel[r.status] || r.status}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                {r.remakeInfo?.prosthesisType || '재제작'} · {r.remakeInfo?.remakeReason || ''}
                            </span>
                            <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>{fmtDate(r.createdAt)}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                            {'선택 기공소: '}{(r.selectedLabNames || []).join(', ') || '-'}
                        </div>
                        {r.status === 'connected' && r.assignedLabName && (
                            <div style={{ fontSize: 12, color: '#059669', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Handshake size={13} /> {r.assignedLabName}{'와(과) 연결되었습니다. [설정 > 거래처 관리]에서 확인하세요.'}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div style={S.wrap}>
            <button style={S.backBtn} onClick={() => navigate(-1)}><ArrowLeft size={16} /> 뒤로</button>

            <div style={S.headerRow}>
                <div style={S.headerIcon}><ClipboardList size={22} color="#fff" /></div>
                <div>
                    <h1 style={S.title}>A/S 재제작 상담·연결 신청</h1>
                    <p style={S.subtitle}>회원 기공소를 선택해 재제작 상담을 신청하면, 덴탈클러스터가 확인 후 연결해 드립니다.</p>
                </div>
            </div>

            {/* 안내 배너 */}
            <div style={S.infoBanner}>
                <Info size={16} color="#2563eb" style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                    {'여러 기공소를 선택할 수 있습니다. 신청 내역은 '}<strong>{'덴탈클러스터(관리자)가 확인'}</strong>{'해 한 곳을 연결해 드립니다. '}
                    {'기공소 연락처는 연결 전까지 공개되지 않습니다.'}
                </div>
            </div>

            {/* 재제작 내용 입력 */}
            <div style={S.card}>
                <h2 style={S.sectionTitle}>재제작 내용</h2>
                <div style={S.formGrid}>
                    <div>
                        <label style={S.label}>원 보철 종류 <span style={S.req}>*</span></label>
                        <select style={S.input} value={prosthesisType} onChange={e => setProsthesisType(e.target.value)}>
                            <option value="">선택</option>
                            {PROSTHESIS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={S.label}>재제작 사유 <span style={S.req}>*</span></label>
                        <select style={S.input} value={remakeReason} onChange={e => setRemakeReason(e.target.value)}>
                            <option value="">선택</option>
                            {REMAKE_REASONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={S.label}>치식 (예: #16, #26)</label>
                        <input style={S.input} value={teeth} onChange={e => setTeeth(e.target.value)} placeholder="#16, #26" />
                    </div>
                    <div>
                        <label style={S.label}>쉐이드 (예: A2)</label>
                        <input style={S.input} value={shade} onChange={e => setShade(e.target.value)} placeholder="A2" />
                    </div>
                </div>
                <div style={{ marginTop: 12 }}>
                    <label style={S.label}>특이사항</label>
                    <textarea style={{ ...S.input, minHeight: 72, resize: 'vertical', fontFamily: 'inherit' }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="재제작 시 참고할 내용을 입력하세요." />
                </div>
                <div style={S.privacyNote}>
                    <Info size={13} color="#94a3b8" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>{'환자 실명·주민번호 등 개인 식별정보는 입력하지 마세요. 치식·보철 종류만으로 상담이 가능합니다.'}</span>
                </div>
            </div>

            {/* 기공소 명단 선택 */}
            <div style={S.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <h2 style={S.sectionTitle}>기공소 선택 {selectedIds.length > 0 && <span style={S.selCount}>{selectedIds.length}곳 선택</span>}</h2>
                </div>

                {/* 위치약관 v1.1 공고 */}
                <PolicyNoticeBanner noticeKey={LBS_TERMS_V11_KEY} fallback={POLICY_NOTICES[LBS_TERMS_V11_KEY]} dismissible />

                {/* 검색·지역 필터 */}
                <div style={S.filterRow}>
                    <div style={S.searchBox}>
                        <Search size={15} color="#94a3b8" />
                        <input style={S.searchInput} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="기공소명·지역 검색" />
                    </div>
                    <select style={S.regionSelect} value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
                        {REGIONS.map(r => <option key={r} value={r}>{r === '전체' ? '지역 전체' : r}</option>)}
                    </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <button type="button" onClick={onLocationButton} disabled={locating}
                        style={{ padding: '6px 12px', background: locating ? '#cbd5e1' : '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: locating ? 'wait' : 'pointer' }}>
                        {locating ? '위치 확인 중...' : '현재 위치로 찾기'}
                    </button>
                    <a href="/location-terms" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'underline' }}>
                        위치기반서비스 이용약관
                    </a>
                    <button type="button" onClick={revokeLocConsent}
                        style={{ padding: 0, background: 'none', border: 'none', fontSize: 11, color: '#94a3b8', textDecoration: 'underline', cursor: 'pointer' }}>
                        위치 이용 동의 철회
                    </button>
                </div>
                {locNotice && <div style={{ ...S.sortNote, color: '#075985' }}>{locNotice}</div>}
                {userLocation && <div style={S.sortNote}>가까운 순(거리 기준)으로 정렬됩니다.</div>}
                {showLocConsent && (
                    <LocationConsentModal title="현재 위치로 기공소 찾기" onAgree={agreeLocConsent} onDecline={declineLocConsent} />
                )}

                {loading ? (
                    <div style={S.empty}>기공소를 불러오는 중...</div>
                ) : visibleLabs.length === 0 ? (
                    <div style={S.empty}>조건에 맞는 기공소가 없습니다.</div>
                ) : (
                    <div style={S.labGrid}>
                        {visibleLabs.map(lab => {
                            const checked = selectedIds.includes(lab.id);
                            return (
                                <div key={lab.id} style={{ ...S.labCard, ...(checked ? S.labCardChecked : {}) }} onClick={() => toggleSelect(lab.id)}>
                                    <div style={{ ...S.checkbox, ...(checked ? S.checkboxOn : {}) }}>
                                        {checked && <CheckCircle size={16} color="#fff" />}
                                    </div>
                                    <div style={S.labAvatar}>
                                        {lab.profileImageUrl
                                            ? <img src={lab.profileImageUrl} alt="" style={S.labAvatarImg} />
                                            : <Building2 size={20} color="#94a3b8" />}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={S.labName}>{lab.businessName}</div>
                                        {lab.slogan && <div style={S.labSlogan}>{lab.slogan}</div>}
                                        {lab.address && (
                                            <div style={S.labAddr}><MapPin size={11} color="#94a3b8" /> {extractRegion(lab.address) || lab.address}</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div style={S.contactHint}>기공소 연락처는 연결 전까지 표시되지 않습니다.</div>
            </div>

            {/* 제출 */}
            <div style={S.submitBar}>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                    {selectedIds.length > 0
                        ? `${selectedIds.length}곳 선택됨 · 재제작 내용 입력 후 신청하세요`
                        : '기공소를 1곳 이상 선택하세요'}
                </div>
                <button style={{ ...S.primaryBtn, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }} disabled={!canSubmit} onClick={handleSubmit}>
                    <Send size={16} /> {submitting ? '신청 중...' : '재제작 상담 신청'}
                </button>
            </div>

            {/* 내 신청 내역 */}
            <div style={S.card}>
                <h2 style={S.sectionTitle}><Clock size={16} style={{ verticalAlign: -2, marginRight: 4 }} />내 신청 내역</h2>
                {renderMyRequests()}
            </div>
        </div>
    );
}

const S = {
    wrap: { maxWidth: 920, margin: '0 auto', padding: '20px 16px 60px' },
    backBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '4px 0', marginBottom: 8 },
    headerRow: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 },
    headerIcon: { width: 44, height: 44, borderRadius: 12, background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    title: { margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' },
    subtitle: { margin: '4px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.6 },
    infoBanner: { display: 'flex', gap: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '12px 14px', fontSize: 13, color: '#1e40af', lineHeight: 1.6, marginBottom: 16 },
    card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, marginBottom: 16 },
    sectionTitle: { margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#0f172a' },
    selCount: { fontSize: 12, fontWeight: 700, color: '#059669', marginLeft: 6, background: '#ecfdf5', padding: '2px 8px', borderRadius: 20 },
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
    req: { color: '#ef4444' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none', background: '#fff' },
    privacyNote: { display: 'flex', gap: 6, marginTop: 12, padding: '8px 10px', background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#64748b', lineHeight: 1.5 },
    filterRow: { display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: 180, display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', background: '#fff' },
    searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 14, background: 'transparent' },
    regionSelect: { border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 14, background: '#fff' },
    sortNote: { fontSize: 12, color: '#94a3b8', marginBottom: 8 },
    labGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 },
    labCard: { position: 'relative', display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 12px 12px 14px', cursor: 'pointer', background: '#fff', transition: 'all 0.12s' },
    labCardChecked: { borderColor: '#059669', background: '#f0fdf4', boxShadow: '0 0 0 1px #059669' },
    checkbox: { position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: 6, border: '2px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' },
    checkboxOn: { background: '#059669', borderColor: '#059669' },
    labAvatar: { width: 42, height: 42, borderRadius: 10, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' },
    labAvatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    labName: { fontSize: 14, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 24 },
    labSlogan: { fontSize: 12, color: '#6366f1', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    labAddr: { display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, color: '#94a3b8', marginTop: 3 },
    contactHint: { fontSize: 12, color: '#94a3b8', marginTop: 10, textAlign: 'center' },
    submitBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '14px 18px', marginBottom: 16, position: 'sticky', bottom: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' },
    primaryBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#059669', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
    ghostBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#475569', border: '1px solid #e2e8f0', padding: '11px 20px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
    empty: { padding: '28px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 },
    reqCard: { border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', background: '#fff' },
    statusBadge: { fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 6 },
    // 완료 화면
    doneCard: { maxWidth: 560, margin: '40px auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 18, padding: '36px 28px', textAlign: 'center' },
    doneIcon: { width: 72, height: 72, borderRadius: '50%', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' },
    doneTitle: { margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' },
    doneDesc: { fontSize: 14, color: '#475569', lineHeight: 1.7, marginTop: 10 },
};

export default AsRemakeRequest;
